import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import {
  getGetPlanningQueryKey,
  GetPlanningQueryResult,
  useGetPlanning,
  useUpdatePlanning,
} from '@/api/generated';
import {
  BerthMove,
  moveShipVisit,
} from '@/components/blocks/berth-plan/berth-plan.utils';
import { ShipVisit } from '@/routes/(app)/(scenarios)/(create)/types';

const PLANNING_PARAMS = { include_historic: true };

/**
 * The planning query cache is the single source of truth for the table and
 * the Berth Plan. Edits land in the cache first, then save in the background.
 * Saves run one at a time in send order, so the last edit wins on the server;
 * once none are left the planning refetches to pick up server-computed fields.
 */
export const usePlanningVisits = (planningId: string) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: planning, isLoading } = useGetPlanning(
    planningId,
    PLANNING_PARAMS
  );
  // A shared scope queues saves instead of racing them.
  const { mutateAsync: updatePlanning, isPending } = useUpdatePlanning({
    mutation: { scope: { id: `update-planning-${planningId}` } },
  });
  const pendingSaves = useRef(0);

  const commitVisits = useCallback(
    async (update: (visits: Array<ShipVisit>) => Array<ShipVisit>) => {
      const queryKey = getGetPlanningQueryKey(planningId, PLANNING_PARAMS);
      await queryClient.cancelQueries({ queryKey });
      const current =
        queryClient.getQueryData<GetPlanningQueryResult>(queryKey);
      if (!current) return;

      // Built from the cache at send time, so overlapping saves each carry
      // every change made so far and the last one wins.
      const shipVisits = update(current.ship_visits ?? []);
      queryClient.setQueryData<GetPlanningQueryResult>(queryKey, {
        ...current,
        ship_visits: shipVisits,
      });

      pendingSaves.current += 1;
      try {
        await updatePlanning({
          planningId,
          data: { ship_visits: shipVisits },
        });
      } catch {
        toast.error(t('general.toast.somethingWentWrong'));
      } finally {
        pendingSaves.current -= 1;
        // Refetching mid-queue would overwrite edits still waiting to save.
        if (pendingSaves.current === 0) {
          await queryClient.invalidateQueries({ queryKey });
        }
      }
    },
    [planningId, queryClient, updatePlanning, t]
  );

  const updateVisit = useCallback(
    async (visitId: string, patch: Partial<ShipVisit>) =>
      commitVisits((visits) =>
        visits.map((visit) =>
          visit.id === visitId ? { ...visit, ...patch } : visit
        )
      ),
    [commitVisits]
  );

  const moveVisit = useCallback(
    async (visitId: string, move: BerthMove) =>
      commitVisits((visits) =>
        visits.map((visit) =>
          visit.id === visitId
            ? { ...visit, ...moveShipVisit(visit, move) }
            : visit
        )
      ),
    [commitVisits]
  );

  return {
    planning,
    isLoading,
    isSaving: isPending,
    commitVisits,
    updateVisit,
    moveVisit,
  };
};
